import { gql } from '@apollo/client';

// Query to get a single service type by ID
export const GET_SERVICE_TYPE = gql`
  query GetServiceType($id: ID!) {
    serviceType(id: $id) {
      id
      title
      color
      category
    }
  }
`;

// Query to get all service types
export const GET_ALL_SERVICE_TYPES = gql`
  query GetAllServiceTypes {
    serviceTypes {
      id
      title
      color
      category
    }
  }
`;

/**
 * Clear all service type data from Apollo cache
 * @param {Object} client - Apollo client instance
 */
export const clearServiceTypeCache = (client) => {
  if (!client) {
    console.error('Apollo client not provided to clearServiceTypeCache');
    return;
  }

  try {
    // First try to identify and evict all ServiceType entities
    const cache = client.cache;
    
    // Clear cache for service types query
    client.cache.evict({ fieldName: 'serviceTypes' });
    
    // Clear cache for individual service type queries
    client.cache.evict({ fieldName: 'serviceType' });
    
    // Run garbage collection
    client.cache.gc();
    
    console.log('Service type cache cleared successfully');
  } catch (error) {
    console.error('Error clearing service type cache:', error);
  }
};

/**
 * Force fetch a service type by ID
 * @param {Object} client - Apollo client instance
 * @param {String} id - Service type ID
 * @returns {Promise<Object>} - The service type data
 */
export const forceFetchServiceType = async (client, id) => {
  if (!client || !id) {
    console.error('Missing required parameters for forceFetchServiceType');
    return null;
  }

  try {
    // Clear any cached data for this service type
    client.cache.evict({ 
      id: `ServiceType:${id}`,
      broadcast: false
    });
    client.cache.gc();
    
    // Fetch from network
    const result = await client.query({
      query: GET_SERVICE_TYPE,
      variables: { id },
      fetchPolicy: 'network-only'
    });
    
    return result.data.serviceType;
  } catch (error) {
    console.error(`Error force fetching service type ${id}:`, error);
    return null;
  }
};

/**
 * Extract service type ID from various formats
 * @param {String|Object} serviceType - Service type as ID string or object
 * @returns {String|null} - The service type ID or null
 */
export const getServiceTypeId = (serviceType) => {
  if (!serviceType) return null;
  
  // If it's a string, assume it's already an ID
  if (typeof serviceType === 'string') {
    return serviceType;
  }
  
  // If it's an object with an id field
  if (typeof serviceType === 'object' && serviceType.id) {
    return serviceType.id;
  }
  
  return null;
};

/**
 * Get border color class based on service type color
 * @param {Object} serviceType - The service type object with color
 * @param {String} fallbackCategory - The job category to use as fallback if no service type
 * @returns {String} - The Tailwind border color class
 */
export const getServiceTypeBorderColor = (serviceType, fallbackCategory = 'CLEANING') => {
  // If we have a service type with color, use it
  if (serviceType?.color) {
    const hexColor = serviceType.color;
    
    // Convert service type hex color to a Tailwind-like style 
    return `border-l-8 relative before:absolute before:left-0 before:top-0 before:bottom-0 before:w-2 before:rounded-l before:bg-[${hexColor}]`;
  }
  
  // Fallback to category-based colors
  switch (fallbackCategory) {
    case 'CLEANING':
      return 'border-l-8 border-blue-500';
    case 'WALKTHROUGH':
      return 'border-l-8 border-purple-500';
    case 'INSPECTION':
    case 'PREARRIVAL_INSPECTION':
      return 'border-l-8 border-green-500';
    default:
      return 'border-l-8 border-gray-500';
  }
}; 